/**
 * Nhãn phân loại thuốc hiển thị trên product card (kê đơn / không kê đơn /
 * thiết yếu / kiểm soát đặc biệt).
 *
 * Dùng chung cho các product card. Áp dụng đầu tiên cho trang /products
 * (ProductCardLite), có thể tái sử dụng cho các card khác về sau.
 *
 * Nhãn render bằng ảnh SVG (không dùng CSS). Chỉ các variant có SVG mới hiển
 * thị nhãn; variant chưa có SVG (prescription — thuốc kê đơn thiếu drugType)
 * trả về icon = undefined và không hiển thị gì.
 */
import {
  MEDICINE_LABEL_NON_PRESCRIPTION,
  MEDICINE_LABEL_PRESCRIPTION_ESSENTIAL,
  MEDICINE_LABEL_PRESCRIPTION_NON_ESSENTIAL,
  MEDICINE_LABEL_SPECIAL,
} from 'constants/Images';

/** Mã sellerSubCategoryCode phân loại thuốc kê đơn / không kê đơn */
export const SELLER_SUB_CATEGORY = {
  /** Thuốc kê đơn */
  PRESCRIPTION: 'LALT0T',
  /** Thuốc không kê đơn */
  NON_PRESCRIPTION: 'XT2U1Y',
} as const;

/** Phân loại thiết yếu của thuốc kê đơn */
export const DRUG_TYPE = {
  ESSENTIAL: 'ESSENTIAL',
  NON_ESSENTIAL: 'NON_ESSENTIAL',
} as const;

/** Loại nhãn phân loại thuốc — mỗi loại có màu hiển thị riêng */
export type MedicineTypeVariant =
  | 'special' // Thuốc kiểm soát đặc biệt
  | 'prescriptionEssential' // Thuốc kê đơn thiết yếu
  | 'prescriptionNonEssential' // Thuốc kê đơn không thiết yếu
  | 'prescription' // Thuốc kê đơn (thiếu drugType)
  | 'nonPrescription'; // Thuốc không kê đơn

/** Ảnh SVG của nhãn — kích thước lấy đúng theo viewBox của file SVG */
export type MedicineTypeIcon = {
  src: string;
  width: number;
  height: number;
};

export type MedicineTypeInfo = {
  label: string;
  variant: MedicineTypeVariant;
  /** Ảnh nhãn; undefined nếu variant chưa có SVG (không hiển thị nhãn) */
  icon?: MedicineTypeIcon;
};

/** Nhãn chắc chắn có SVG để render (icon required) */
export type MedicineTypeLabel = MedicineTypeInfo & { icon: MedicineTypeIcon };

/**
 * Map variant -> ảnh SVG. 4/5 variant có SVG (riêng `prescription` chưa có).
 * height cố định 16px, width lấy đúng theo viewBox từng file SVG (special: 28,
 * prescriptionEssential: 116, prescriptionNonEssential: 149, nonPrescription: 104).
 */
const MEDICINE_TYPE_ICON: Partial<Record<MedicineTypeVariant, MedicineTypeIcon>> = {
  special: { src: MEDICINE_LABEL_SPECIAL, width: 28, height: 16 },
  prescriptionEssential: { src: MEDICINE_LABEL_PRESCRIPTION_ESSENTIAL, width: 116, height: 16 },
  prescriptionNonEssential: { src: MEDICINE_LABEL_PRESCRIPTION_NON_ESSENTIAL, width: 149, height: 16 },
  nonPrescription: { src: MEDICINE_LABEL_NON_PRESCRIPTION, width: 104, height: 16 },
};

type MedicineTypeLabelInput = {
  isIngredientSpecial?: boolean;
  sellerSubCategoryCode?: string;
  drugType?: string;
};

/**
 * Tính danh sách nhãn phân loại thuốc để hiển thị trên card.
 *
 * Quy tắc:
 * 1. isIngredientSpecial === true -> "Thuốc kiểm soát đặc biệt" (special).
 * 2. sellerSubCategoryCode === LALT0T (kê đơn):
 *      - drugType ESSENTIAL      -> "Thuốc kê đơn thiết yếu"
 *      - drugType NON_ESSENTIAL  -> "Thuốc kê đơn không thiết yếu"
 *      - còn lại                 -> "Thuốc kê đơn" (chưa có SVG -> không hiển thị)
 * 3. sellerSubCategoryCode === XT2U1Y -> "Thuốc không kê đơn".
 *
 * Kết hợp: thuốc KSDB mà đồng thời là kê đơn thiết yếu / kê đơn không thiết yếu
 * / không kê đơn sẽ hiển thị CẢ 2 nhãn — special đứng trước, nhãn phân loại kê
 * đơn đứng sau. Riêng kê đơn thiếu drugType: khi đã là KSDB thì không thêm nhãn
 * "Thuốc kê đơn" (giữ nguyên hành vi cũ, và nhãn này cũng chưa có SVG).
 *
 * @returns mảng nhãn theo thứ tự hiển thị; chỉ gồm nhãn có SVG (icon). Mảng
 *          rỗng nếu không thuộc phân loại nào hoặc nhãn chưa có SVG.
 */
export function getMedicineTypeLabels(product?: MedicineTypeLabelInput): MedicineTypeLabel[] {
  if (!product) return [];

  const { isIngredientSpecial, sellerSubCategoryCode, drugType } = product;

  const labels: MedicineTypeLabel[] = [];
  const add = (label: string, variant: MedicineTypeVariant) => {
    const icon = MEDICINE_TYPE_ICON[variant];
    if (icon) labels.push({ label, variant, icon });
  };

  // Thuốc có hoạt chất đặc biệt — có thể đứng riêng hoặc đi kèm nhãn kê đơn
  if (isIngredientSpecial) add('Thuốc kiểm soát đặc biệt', 'special');

  if (sellerSubCategoryCode === SELLER_SUB_CATEGORY.PRESCRIPTION) {
    if (drugType === DRUG_TYPE.ESSENTIAL) add('Thuốc kê đơn thiết yếu', 'prescriptionEssential');
    else if (drugType === DRUG_TYPE.NON_ESSENTIAL) add('Thuốc kê đơn không thiết yếu', 'prescriptionNonEssential');
    // Kê đơn thiếu drugType: chỉ hiển thị nhãn "Thuốc kê đơn" khi KHÔNG phải KSDB
    else if (!isIngredientSpecial) add('Thuốc kê đơn', 'prescription');
  } else if (sellerSubCategoryCode === SELLER_SUB_CATEGORY.NON_PRESCRIPTION) {
    // Không kê đơn: hiển thị kèm nhãn KSDB nếu có (KSDB + không kê đơn -> 2 nhãn)
    add('Thuốc không kê đơn', 'nonPrescription');
  }

  return labels;
}
